import {
  faCube,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { faArrowLeft, faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { Outlet, useMatch } from "react-router-dom";

import { Flow } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { LoadingView } from "src/base-components/LoadingView";
import { EmptyState } from "src/design-system/EmptyState";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import {
  FlowPageParams,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { getResponseStatus } from "src/utils/query";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type FlowPageContext = ReturnType<typeof useFlowContext>;

export type FlowPageContextNoData = Partial<FlowPageContext>;

type FlowLoadErrorProps = {
  error: unknown;
  isRetrying: boolean;
  wsDashboardUrl: string;
  onRetry: () => void;
};

/**
 * A flow fetch can fail for reasons other than the flow being gone, and the
 * distinction matters to the reader: a flow whose response exceeds the API's
 * 1MB cap is still there and still runs, it just cannot be opened, so telling
 * its author it "was not found" sends them hunting for a deletion that never
 * happened.
 */
const FlowLoadError: React.FC<FlowLoadErrorProps> = ({
  error,
  isRetrying,
  wsDashboardUrl,
  onRetry,
}) => {
  const goToDashboard = {
    icon: faArrowLeft,
    label: "Go to Home Page",
    linkTo: wsDashboardUrl,
  };
  const status = getResponseStatus(error);

  if (status === 403 || status === 404) {
    return (
      <EmptyState
        actionButton={goToDashboard}
        dataLoc="flow-not-found"
        description="To see all Decision Flows, please go to the Home page."
        headline="This Decision Flow was not found"
        icon={faCube}
      />
    );
  }

  if (status === 413) {
    return (
      <EmptyState
        actionButton={goToDashboard}
        dataLoc="flow-too-large"
        description="It is over the 1MB response limit, so it cannot be opened. The Decision Flow itself is unaffected and published versions keep running. Deleting draft versions you no longer need brings it back under the limit."
        headline="This Decision Flow is too large to open"
        icon={faTriangleExclamation}
        variant="error"
      />
    );
  }

  return (
    <EmptyState
      actionButton={[
        {
          dataLoc: "flow-load-failed-retry",
          icon: faRotateRight,
          isLoading: isRetrying,
          label: "Try again",
          onClick: onRetry,
        },
        goToDashboard,
      ]}
      dataLoc="flow-load-failed"
      description="Something went wrong while loading this Decision Flow. Please try again."
      headline="This Decision Flow could not be loaded"
      icon={faTriangleExclamation}
      variant="error"
    />
  );
};

export const FlowPage: React.FC = () => {
  const { flow_id, wsId, orgId } = useParamsDecode<FlowPageParams>();
  const flow = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const { workspace, modules } = useWorkspaceContext();
  const isVersionsPage = useMatch("/org/:orgId/ws/:wsId/flows/:flow_id");
  const isReviewCasePage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/review-queue/:case_id",
  );
  const isJobPage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/jobs/:job_id",
  );
  const isInsightsPage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/insights",
  );

  const documentTitle = (
    <DocumentTitle
      segments={
        flow.data?.name
          ? [flow.data.name, "Decision Flows"]
          : ["Decision Flows"]
      }
    />
  );

  if (flow.isError) {
    return (
      <div className="flex w-full items-center justify-center">
        {documentTitle}
        <FlowLoadError
          error={flow.error}
          isRetrying={flow.isFetching}
          wsDashboardUrl={getUrlToWsDashboard({
            orgId,
            wsId,
            page: WsDashboardPageCode.Flows,
          })}
          onRetry={() => flow.refetch()}
        />
      </div>
    );
  }

  if (isInsightsPage) {
    return (
      <>
        {documentTitle}
        <Outlet context={{ workspace, modules, orgId }} />
      </>
    );
  }

  const isStandalonePage = isReviewCasePage || isJobPage;

  if (isStandalonePage) {
    return (
      <>
        {documentTitle}
        <LoadingView
          queryResult={flow}
          renderUpdated={(flow: Flow) => (
            <Outlet context={{ flow, workspace, modules, orgId }} />
          )}
        />
      </>
    );
  }

  return (
    <FlowPageLayout flow={flow.data}>
      {documentTitle}
      <LoadingView
        queryResult={flow}
        renderUpdated={(flow: Flow) => (
          <Outlet context={{ flow, workspace, modules, orgId }} />
        )}
        renderUpdating={() =>
          isVersionsPage ? (
            <Outlet context={{ workspace, modules, orgId }} />
          ) : (
            <></>
          )
        }
      />
    </FlowPageLayout>
  );
};
